import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { BannerForm } from "./banner-form";

import { FormError, SideSheet, notify } from "@/components/kit";
import { useUpdateBanner } from "@/hooks/api/banner/useUpdateBanner";
import { useIsUploadingBannerImage } from "@/hooks/api/banner/useUploadBannerImage";
import {
  BANNER_FORM_SCHEMA,
  BannerFormData,
  EMPTY_BANNER_FORM,
} from "@/schemas/banner-form.schema";
import { Banner, UpdateBanner } from "@/types";
import { getApiErrorMessage } from "@/utils/api-error";
import { buildBannerLink, parseBannerLink } from "@/utils/banner-link";

interface EditBannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  banner: Banner | null;
}

export function EditBannerModal({
  isOpen,
  onClose,
  banner,
}: EditBannerModalProps) {
  const form = useForm<BannerFormData>({
    resolver: zodResolver(BANNER_FORM_SCHEMA),
    mode: "all",
    defaultValues: EMPTY_BANNER_FORM,
  });

  const {
    mutate: updateBanner,
    isPending: isUpdatingBanner,
    error,
    reset: resetMutation,
  } = useUpdateBanner();
  const isUploadingImage = useIsUploadingBannerImage();

  useEffect(() => {
    if (!banner) return;

    form.reset({
      imageUrl: banner.imageUrl,
      target: parseBannerLink(banner.link, banner.linkType),
    });
    resetMutation();
  }, [banner]);

  const handleSubmit = (data: BannerFormData) => {
    if (!banner) return;

    const { link, linkType } = buildBannerLink(data.target);
    const changes: UpdateBanner = {
      ...(data.imageUrl !== banner.imageUrl && { imageUrl: data.imageUrl }),
      ...(link !== banner.link && { link }),
      ...(linkType !== banner.linkType && { linkType }),
    };

    if (!Object.keys(changes).length) {
      onClose();

      return;
    }

    updateBanner(
      { id: banner.id, data: changes },
      {
        onSuccess: () => {
          notify.success("Banner updated");
          onClose();
        },
      },
    );
  };

  return (
    <FormProvider {...form}>
      <SideSheet
        description="Changes show on the app and website home page"
        isOpen={isOpen}
        isSubmitDisabled={!form.formState.isDirty || isUploadingImage}
        isSubmitting={isUpdatingBanner}
        submitLabel="Save"
        title="Edit banner"
        onClose={onClose}
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <FormError
          message={
            error
              ? getApiErrorMessage(
                  error,
                  "An error occurred while updating the banner, please try again!",
                )
              : null
          }
        />
        <BannerForm />
      </SideSheet>
    </FormProvider>
  );
}
